import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, record } from 'fp-ts';
import { createAction } from '@mews/framework';
import { Company, Customer } from '@mews-commander/data-access';
import { ApplicationState, CommanderThunkAction, AsyncAction, Option } from '@mews-commander/types';
import { keyById } from '@mews/fp';
import { getOrFalse, createFetchAction, getFullName, getFullNameWithSecondLastName } from '@mews-commander/utils';
import prefix from '../prefix';
import parentSelector from '../selector';
import { createEntityFormattedMetaSelector } from '../selectors/form';
import { createEntitiesReducer, createBaseEntitySelectors, createEntitySelectorByCompanyId } from './utils';
import { getCompanyName, getCompaniesByIds } from './companies';

export const NAME = 'customers';

const CUSTOMERS_LOADED = prefix('CUSTOMERS_LOADED');
const CUSTOMER_ADD = prefix('CUSTOMER_ADD');
const CUSTOMERS_DELETED = prefix('CUSTOMERS_DELETED');

export const addCustomer =
    (customer: Customer): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const { customerComponent } = container();
        const promise = customerComponent.add(customer);
        return dispatch(createFetchAction(CUSTOMER_ADD, promise));
    };

export const setCustomers = (customers: Customer[]) => createAction(CUSTOMERS_LOADED, { entities: customers });

export const customersReducer = createEntitiesReducer({
    FETCH_SUCCESS: CUSTOMERS_LOADED,
    DELETE_SUCCESS: CUSTOMERS_DELETED,
});

export const [getCustomers, getCustomersByIds, getCustomersByOwnerId, getCustomer] = createBaseEntitySelectors<
    ApplicationState,
    Customer,
    typeof NAME
>(parentSelector, NAME);

export const getCustomerIds = createSelector(
    getCustomers,
    flow(
        Object.values<Customer>,
        array.map(({ id }) => id)
    )
);

export const getCustomerFullName = flow(getCustomer, getFullName);
export const getCustomerImageId = createSelector(getCustomer, (customer) => customer?.imageId);

export const getCustomerFullNameWithSecondLastName = createSelector(getCustomer, getFullNameWithSecondLastName);

export const getCustomerOrCompanyFullName = createSelector(
    getCustomerFullName,
    getCompanyName,
    (customerName, companyName) => customerName || companyName
);

export const getCustomersFullNames = createSelector(getCustomersByIds, flow(keyById, record.map(getFullName)));

export const getCustomersOrCompaniesFullNames = createSelector(
    getCustomersByIds,
    getCompaniesByIds,
    (customers, companies) => {
        const customersFullNames = pipe(customers, keyById, record.map(getFullName)) as Record<string, string>;
        const companiesFullNames = pipe(
            companies,
            keyById,
            record.map((company: Company) => company.name)
        ) as Record<string, string>;
        return { ...customersFullNames, ...companiesFullNames };
    }
);

export const getCustomerOptions = flow(getCustomer, (customer) => customer?.options);

export const isCustomerInvoiceable = createSelector(getCustomerOptions, getOrFalse('invoiceable'));

export const getCustomersByCompanyId = createEntitySelectorByCompanyId(getCustomers);

export const getCustomerIdsByCompanyId = createSelector(
    getCustomersByCompanyId,
    array.map(({ id }) => id)
);

export const getCustomerClassifications = flow(getCustomer, (customer) => customer?.classifications);

export const getCustomerNotes = flow(getCustomer, (customer) => customer?.notes);

export const getCustomerFormattedMeta = createEntityFormattedMetaSelector(getCustomer);

export const getCustomerUserId = flow(getCustomer, (customer) => customer?.userId);

export const getCustomerPreferredLanguageCode = flow(getCustomer, (customer) => customer?.preferredLanguageCode);

export const getCustomerNativeLanguageCode = flow(getCustomer, (customer) => customer?.nativeLanguageCode);

export const getCustomerEmail = flow(getCustomer, (customer) => customer?.email);

export const getCustomersOptions = (state: ApplicationState, customerIds: string[]) =>
    getCustomersByIds(state, { ids: customerIds }).map(customerToOption);

export const customerToOption = (customer: Customer): Option => ({
    value: customer.id,
    label: getFullName(customer),
    disabled: false,
    inactive: false,
    indent: false,
    secondaryText: null,
});
